import React, { PropsWithChildren, useMemo } from "react";

import { TodoBottomSheet } from "./TodoBottomSheet";

type ReportTodoIssueBottomSheetRef = {
  close: () => void;
  present: (newlyCreated: boolean, issueId: string) => void;
  createIssue: (taskExecutionId: string, taskTitle: string) => void;
};

type TodoBottomSheetContextType = ReportTodoIssueBottomSheetRef & {
  openTodoEditCategory: (todoId: string) => void;
};

const TodoBottomSheetContext =
  React.createContext<TodoBottomSheetContextType | null>(null);

export const TodoBottomSheetProvider = React.memo(InnerTodoBottomSheetProvider);

function InnerTodoBottomSheetProvider({
  children,
  position,
  onBottomSheetClosed,
  onBottomSheetOpen,
  openTodoEditCategory,
  onShowFile,
  openProductDetail,
}: PropsWithChildren<{
  position?: "left" | "right";
  onBottomSheetClosed?(): void;
  onBottomSheetOpen?(): void;
  openTodoEditCategory: (todoId: string) => void;
  onShowFile: (id: string) => void;
  openProductDetail: (productId: string) => void;
}>) {
  const createRef = React.useRef<ReportTodoIssueBottomSheetRef>(null);
  const openRef = React.useRef<ReportTodoIssueBottomSheetRef>(null);

  const value: TodoBottomSheetContextType = useMemo(
    () => ({
      close() {
        createRef.current?.close();
        openRef.current?.close();
      },
      present(newlyCreated, issueId) {
        onBottomSheetOpen?.();
        openRef.current?.present(newlyCreated, issueId);
      },
      createIssue(taskExecutionId, taskTitle) {
        createRef.current?.createIssue(
          taskExecutionId,

          taskTitle
        );
      },
      openTodoEditCategory(todoId) {
        openTodoEditCategory(todoId);
      },
    }),
    [onBottomSheetOpen, openTodoEditCategory]
  );

  return (
    <>
      <TodoBottomSheetContext.Provider value={value}>
        {children}
      </TodoBottomSheetContext.Provider>
      <TodoBottomSheet
        onShowFile={onShowFile}
        position={position}
        openExpanded={false}
        ref={createRef}
        onDismiss={onBottomSheetClosed}
        openTodoEditCategory={openTodoEditCategory}
        openProductDetail={openProductDetail}
      />
      <TodoBottomSheet
        onShowFile={onShowFile}
        position={position}
        openExpanded
        ref={openRef}
        onDismiss={onBottomSheetClosed}
        openTodoEditCategory={openTodoEditCategory}
        openProductDetail={openProductDetail}
      />
    </>
  );
}

export function useTodoBottomSheet() {
  const context = React.useContext(TodoBottomSheetContext);
  if (!context) {
    throw new Error(
      "useTodoBottomSheet must be used within a TodoBottomSheetProvider"
    );
  }
  return context;
}
